import React from 'react';
import { IntlProvider } from 'react-intl';
import { messages, DEFAULT_LOCALE, getLocaleFromBrowser, getLocaleDirection } from '../i18n';

const LanguageContext = React.createContext(null);

const ALL_LOCALES = [
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'en', name: 'English', flag: '🇬🇧', dir: 'ltr' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'pt', name: 'Português', flag: '🇵🇹', dir: 'ltr' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', dir: 'ltr' },
  { code: 'bg', name: 'Български', flag: '🇧🇬', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', dir: 'rtl' },
  { code: 'lb', name: 'Lëtzebuergesch', flag: '🇱🇺', dir: 'ltr' }
];

export function useLanguage() {
  const context = React.useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
}

export function LanguageProvider({ children }) {
  const [locale, setLocale] = React.useState(() => {
    const saved = localStorage.getItem('drbooking_locale');
    return saved || getLocaleFromBrowser();
  });

  React.useEffect(() => {
    localStorage.setItem('drbooking_locale', locale);
    document.documentElement.dir = getLocaleDirection(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const changeLanguage = React.useCallback((newLocale) => {
    const found = ALL_LOCALES.find((l) => l.code === newLocale);
    if (found) {
      setLocale(newLocale);
    }
  }, []);

  // Simple translation function - falls back to French then to key id
  const t = React.useCallback((id, values) => {
    const currentMessages = messages[locale] || messages[DEFAULT_LOCALE] || {};
    const fallbackMessages = messages['fr'] || {};
    let text = currentMessages[id] || fallbackMessages[id] || id;
    if (values) {
      Object.keys(values).forEach((key) => {
        text = text.replace(new RegExp(`{${key}}`, 'g'), values[key]);
      });
    }
    return text;
  }, [locale]);

  const value = React.useMemo(() => ({
    locale,
    language: locale,
    changeLanguage,
    setLanguage: changeLanguage,
    t,
    supportedLocales: ALL_LOCALES,
    direction: getLocaleDirection(locale),
    isRTL: getLocaleDirection(locale) === 'rtl'
  }), [locale, changeLanguage, t]);

  const currentMessages = messages[locale] || messages[DEFAULT_LOCALE];

  return (
    <IntlProvider 
      locale={locale} 
      messages={currentMessages}
      defaultLocale={DEFAULT_LOCALE}
      onError={() => {}}
    >
      <LanguageContext.Provider value={value}>
        {children}
      </LanguageContext.Provider>
    </IntlProvider>
  );
}
